import { useMutation } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { useState } from "react";
import { toast } from "react-toastify";
import { Button, ButtonVariant, Hide, Input, Radio } from "shared/components";
import { Card } from "shared/components/Card";
import {
  CheckoutSubmissionType,
  PreferredSubmissionType,
  SubmissionType
} from "shared/types";

type Configuration = {
  preferredSubmissionType?: PreferredSubmissionType;
  preferredCheckoutType?: CheckoutSubmissionType;
  email?: string;
};

export const Configuration = ({
  preferredSubmissionType,
  preferredCheckoutType,
  isFreeCheckout,
  onSuccess,
  applicationId,
  applicantId
}: {
  preferredSubmissionType?: PreferredSubmissionType;
  preferredCheckoutType?: CheckoutSubmissionType;
  onSuccess: () => void;
  applicationId: number;
  applicantId: number;
  isFreeCheckout?: boolean;
}) => {
  const [configuration, setConfiguration] = useState({
    preferredSubmissionType: preferredSubmissionType,
    preferredCheckoutType: isFreeCheckout
      ? SubmissionType.InPerson
      : preferredCheckoutType,
    email: ""
  });

  const mutation = useMutation({
    mutationKey: [
      "MANUAL_APPLICATION_CONFIGURATION",
      applicantId,
      applicationId
    ],
    mutationFn: (configuration: Configuration) =>
      instance.patch(
        `/portal/v1/applications/${applicationId}/applicants/${applicantId}/update_preferred_manual_values`,
        {
          email: configuration.email,
          preferred_submission_disclosure_type:
            configuration.preferredSubmissionType,
          preferred_checkout_type: configuration.preferredCheckoutType
        }
      ),
    onSuccess: () => {
      if (
        configuration.preferredSubmissionType === "send_by_email" &&
        configuration.preferredCheckoutType === "send_by_email"
      )
        toast.success(
          "Checkout and submission disclosure emails will be sent to the applicant."
        );
      else if (configuration.preferredSubmissionType === "send_by_email")
        toast.success(
          "Submission disclosure email will be sent to the applicant."
        );
      else if (configuration.preferredCheckoutType === "send_by_email")
        toast.success(
          "Checkout email will be sent to the applicant. Please make sure that the applicant has access to the email."
        );

      onSuccess();
    }
  });

  return (
    <form
      className="bg-background-900 h-[calc(80vh-72px)]"
      onSubmit={(e) => {
        e.preventDefault();

        mutation.mutate(configuration);
      }}
    >
      <div className=" max-w-[440px] mx-auto h-[calc(80vh-152px)] pb-4 overflow-y-auto">
        <h2 className="text-xl font-bold text-content-1000 py-4">
          Submission disclosure
        </h2>
        <Card className="divide-y-[1px] divide-alpha-40">
          <Radio
            id="submission-type-by-email"
            name="submission-type"
            className="p-4"
            label={
              <div>
                <p className="text-sm font-medium text-content-1000 mb-1">
                  Send disclosure to applicant
                </p>
                <p className="text-content-300 text-xs">
                  Send a signing request via email to the applicant
                </p>
              </div>
            }
            checked={
              configuration.preferredSubmissionType ===
              SubmissionType.SendByEmail
            }
            onChange={() => {
              setConfiguration((prev) => ({
                ...prev,
                preferredSubmissionType: SubmissionType.SendByEmail
              }));
            }}
          />

          <Radio
            id="submission-type-in-person"
            name="submission-type"
            className="p-4"
            label={
              <div>
                <p className="text-sm font-medium text-content-1000 mb-1">
                  Sign here by applicant
                </p>
                <p className="text-content-300 text-xs">
                  Applicant will sign the disclosure here
                </p>
              </div>
            }
            checked={
              configuration.preferredSubmissionType === SubmissionType.InPerson
            }
            onChange={() => {
              setConfiguration((prev) => ({
                ...prev,
                preferredSubmissionType: SubmissionType.InPerson
              }));
            }}
          />
        </Card>

        <Hide if={isFreeCheckout}>
          <h2 className="text-xl font-bold text-content-1000 mb-4 mt-8">
            Checkout
          </h2>
          <Card className="divide-y-[1px] divide-alpha-40">
            <Radio
              id="checkout-type-paid-in-cash"
              name="checkout-type"
              className="p-4"
              label={
                <div>
                  <p className="text-sm font-medium text-content-1000 mb-1">
                    Applicant has or will pay in-person
                  </p>
                  <p className="text-content-300 text-xs">
                    Screening cost will be charged to the property&apos;s linked
                    bank account
                  </p>
                </div>
              }
              checked={
                configuration.preferredCheckoutType ===
                SubmissionType.CheckoutPaidInCash
              }
              onChange={() => {
                setConfiguration((prev) => ({
                  ...prev,
                  preferredCheckoutType: SubmissionType.CheckoutPaidInCash
                }));
              }}
            />

            <Radio
              id="checkout-type-in-person"
              name="checkout-type"
              className="p-4"
              label={
                <div>
                  <p className="text-sm font-medium text-content-1000 mb-1">
                    Pay here by applicant&apos;s card
                  </p>
                  <p className="text-content-300 text-xs">
                    Enter applicant&apos;s card details to pay application fee
                  </p>
                </div>
              }
              checked={
                configuration.preferredCheckoutType === SubmissionType.InPerson
              }
              onChange={() => {
                setConfiguration((prev) => ({
                  ...prev,
                  preferredCheckoutType: SubmissionType.InPerson
                }));
              }}
            />

            <Radio
              id="checkout-type-by-email"
              name="checkout-type"
              className="p-4"
              label={
                <div>
                  <p className="text-sm font-medium text-content-1000 mb-1">
                    Send payment request to applicant{" "}
                  </p>
                  <p className="text-content-300 text-xs">
                    Send out a payment request via email to the applicant
                  </p>
                </div>
              }
              checked={
                configuration.preferredCheckoutType ===
                SubmissionType.SendByEmail
              }
              onChange={() => {
                setConfiguration((prev) => ({
                  ...prev,
                  preferredCheckoutType: SubmissionType.SendByEmail
                }));
              }}
            />
          </Card>
        </Hide>

        {
          // if any sendVia mail -- show email input

          [
            configuration.preferredCheckoutType,
            configuration.preferredSubmissionType
          ].includes(SubmissionType.SendByEmail) ? (
            <div className="mt-4 animate-appear">
              <h2 className="text-xl font-bold text-content-1000 py-4 pb-0">
                Please add email
              </h2>

              <p className="text-sm text-content-400 font-medium mb-4">
                {configuration.preferredCheckoutType ===
                  SubmissionType.SendByEmail &&
                configuration.preferredSubmissionType ===
                  SubmissionType.SendByEmail ? (
                  <>
                    Applicant will receive payment request and submission
                    disclosure via email
                  </>
                ) : configuration.preferredCheckoutType ===
                  SubmissionType.SendByEmail ? (
                  <>Applicant will receive payment request via email</>
                ) : configuration.preferredSubmissionType ===
                  SubmissionType.SendByEmail ? (
                  <>Applicant will receive submission disclosure via email</>
                ) : null}
              </p>
              <Input
                error={!configuration.email.length}
                id="applicant-email"
                className="w-[300px] !mb-0"
                placeholder="johndoe@example.com"
                hideOptional
                type="email"
                value={configuration.email}
                onChange={(e) => {
                  setConfiguration((prev) => ({
                    ...prev,
                    email: e.target.value
                  }));
                }}
              />
            </div>
          ) : null
        }
      </div>

      <div className="h-20 border-t border-alpha-100 bg-white gap-2 flex justify-end items-center pr-4">
        <Button
          tooltipZIndex={51}
          variant={ButtonVariant.PRIMARY}
          disabled={
            !configuration.preferredCheckoutType ||
            !configuration.preferredSubmissionType ||
            ([
              configuration.preferredCheckoutType,
              configuration.preferredSubmissionType
            ].includes(SubmissionType.SendByEmail) &&
              !configuration.email)
          }
          tooltip={
            !configuration.preferredCheckoutType
              ? "Please select checkout configuration"
              : !configuration.preferredSubmissionType
                ? "Please select submission configuration"
                : [
                      configuration.preferredCheckoutType,
                      configuration.preferredSubmissionType
                    ].includes(SubmissionType.SendByEmail) &&
                    !configuration.email
                  ? "Please enter applicant email"
                  : undefined
          }
          className="w-[125px]"
          type="submit"
          loading={mutation.isPending}
        >
          Submit
        </Button>
      </div>
    </form>
  );
};
